import { useEffect, useState, useCallback } from "react";
import api, { money, fileUrl } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, X, ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CATS = [
  { id: "jollof", name: "Rice Dishes" },
  { id: "amala-abula", name: "Specials" },
  { id: "fufu-soups", name: "Fufu & Soups" },
  { id: "combos", name: "Combos" },
  { id: "sides", name: "Sides" },
  { id: "grocery", name: "African Grocery" },
];

const blank = { name: "", category: "jollof", description: "", base_price: 0, available: true, hidden: false, option_groups: [], sort_order: 0 };

export default function MenuManagerPanel({ canEdit = true }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/menu");
      setItems(data);
    } catch (e) {
      console.error("Failed to load menu:", e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleAvail = async (id) => {
    try {
      const { data } = await api.patch(`/admin/menu/${id}/availability`);
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, available: data.available } : i)));
    } catch (e) { toast.error("Failed"); }
  };

  const openNew = () => { setEditing({ ...blank }); setDialogOpen(true); };
  const openEdit = (item) => { setEditing(JSON.parse(JSON.stringify(item))); setDialogOpen(true); };

  const save = async () => {
    const payload = { ...editing, base_price: Number(editing.base_price), sort_order: Number(editing.sort_order || 0) };
    try {
      if (editing.id) await api.put(`/admin/menu/${editing.id}`, payload);
      else await api.post("/admin/menu", payload);
      toast.success("Saved");
      setDialogOpen(false);
      load();
    } catch (e) { toast.error("Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`/admin/menu/${id}`); load(); toast.success("Deleted"); } catch (e) { toast.error("Failed"); }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    if (!editing?.id) { toast.error("Save the item first, then add a photo"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post(`/admin/menu/${editing.id}/image`, fd);
      setEditing((e) => ({ ...e, image_url: data.image_url }));
      setItems((prev) => prev.map((i) => (i.id === editing.id ? { ...i, image_url: data.image_url } : i)));
      toast.success("Photo uploaded");
    } catch (e) { toast.error("Upload failed"); }
    setUploading(false);
  };

  // option group editing helpers
  const addGroup = () => setEditing((e) => ({ ...e, option_groups: [...e.option_groups, { name: "", required: true, options: [{ label: "", price_delta: 0 }] }] }));
  const updGroup = (gi, patch) => setEditing((e) => ({ ...e, option_groups: e.option_groups.map((g, i) => (i === gi ? { ...g, ...patch } : g)) }));
  const rmGroup = (gi) => setEditing((e) => ({ ...e, option_groups: e.option_groups.filter((_, i) => i !== gi) }));
  const addOpt = (gi) => updGroup(gi, { options: [...editing.option_groups[gi].options, { label: "", price_delta: 0 }] });
  const updOpt = (gi, oi, patch) => updGroup(gi, { options: editing.option_groups[gi].options.map((o, i) => (i === oi ? { ...o, ...patch } : o)) });
  const rmOpt = (gi, oi) => updGroup(gi, { options: editing.option_groups[gi].options.filter((_, i) => i !== oi) });

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold text-stone-900">Menu Items</h2>
        {canEdit && (
          <button data-testid="admin-menu-crud-add-button" onClick={openNew} className="flex items-center gap-2 rounded-full bg-orange-700 px-4 py-2 text-sm font-bold text-white hover:bg-orange-800">
            <Plus className="h-4 w-4" /> Add Item
          </button>
        )}
      </div>

      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.id} className="flex items-center justify-between gap-4 rounded-xl border border-stone-200 bg-white p-4" data-testid={`admin-menu-row-${it.id}`}>
            <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-stone-100">
              {it.image_url ? <img src={fileUrl(it.image_url)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center text-stone-300"><ImagePlus className="h-5 w-5" /></div>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-stone-900">{it.name}</span>
                {it.hidden && <span className="rounded bg-stone-200 px-2 py-0.5 text-xs text-stone-600">Hidden</span>}
                <span className="text-xs text-stone-400">{CATS.find((c) => c.id === it.category)?.name}</span>
              </div>
              <div className="truncate text-sm text-stone-500">{money(it.base_price)} · {it.description}</div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className={`text-xs font-semibold ${it.available ? "text-emerald-700" : "text-rose-600"}`}>{it.available ? "Available" : "Sold Out"}</span>
                <Switch data-testid={`admin-toggle-sold-out-switch-${it.id}`} checked={it.available} onCheckedChange={() => toggleAvail(it.id)} />
              </div>
              {canEdit && (
                <>
                  <button onClick={() => openEdit(it)} className="text-stone-500 hover:text-orange-700"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(it.id)} className="text-stone-500 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {canEdit && editing && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="admin-menu-dialog">
            <DialogHeader><DialogTitle className="font-display text-2xl">{editing.id ? "Edit Item" : "New Item"}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Name</Label><Input data-testid="admin-item-name-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-1.5" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATS.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Price ($)</Label><Input data-testid="admin-item-price-input" type="number" step="0.01" value={editing.base_price} onChange={(e) => setEditing({ ...editing, base_price: e.target.value })} className="mt-1.5" /></div>
              </div>
              <div><Label>Description</Label><Textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="mt-1.5 resize-none" rows={2} /></div>

              {/* Dish photo */}
              <div>
                <Label>Dish Photo</Label>
                <div className="mt-1.5 flex items-center gap-4">
                  <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl border border-stone-200 bg-stone-100">
                    {editing.image_url ? (
                      <img src={fileUrl(editing.image_url)} alt="dish" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-stone-300"><ImagePlus className="h-6 w-6" /></div>
                    )}
                  </div>
                  <div className="text-sm">
                    <label className={`inline-flex cursor-pointer items-center gap-2 rounded-full border border-stone-300 px-4 py-2 font-semibold text-stone-700 hover:bg-stone-100 ${!editing.id ? "pointer-events-none opacity-50" : ""}`}>
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                      {editing.image_url ? "Replace Photo" : "Upload Photo"}
                      <input data-testid="admin-item-image-input" type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} />
                    </label>
                    {!editing.id && <p className="mt-1 text-xs text-stone-400">Save the item first to enable photo upload.</p>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm"><Switch checked={editing.available} onCheckedChange={(v) => setEditing({ ...editing, available: v })} /> Available</label>
                <label className="flex items-center gap-2 text-sm"><Switch checked={editing.hidden} onCheckedChange={(v) => setEditing({ ...editing, hidden: v })} /> Hidden</label>
                <div className="flex items-center gap-2 text-sm"><span>Sort</span><Input type="number" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })} className="w-20" /></div>
              </div>

              {/* Option groups */}
              <div className="rounded-xl border border-stone-200 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-bold">Option Groups</span>
                  <button onClick={addGroup} className="text-xs font-semibold text-orange-700 hover:underline">+ Add group</button>
                </div>
                {editing.option_groups.map((g, gi) => (
                  <div key={gi} className="mb-3 rounded-lg bg-stone-50 p-3">
                    <div className="flex items-center gap-2">
                      <Input placeholder="Group name (e.g. Protein)" value={g.name} onChange={(e) => updGroup(gi, { name: e.target.value })} className="flex-1" />
                      <label className="flex items-center gap-1 whitespace-nowrap text-xs"><Switch checked={g.required !== false} onCheckedChange={(v) => updGroup(gi, { required: v })} /> Req</label>
                      <button onClick={() => rmGroup(gi)} className="text-stone-400 hover:text-rose-600"><X className="h-4 w-4" /></button>
                    </div>
                    <div className="mt-2 space-y-1.5">
                      {g.options.map((o, oi) => (
                        <div key={oi} className="flex items-center gap-2">
                          <Input placeholder="Option label" value={o.label} onChange={(e) => updOpt(gi, oi, { label: e.target.value })} className="flex-1" />
                          <Input placeholder="+$" type="number" step="0.01" value={o.price_delta} onChange={(e) => updOpt(gi, oi, { price_delta: Number(e.target.value) })} className="w-24" />
                          <button onClick={() => rmOpt(gi, oi)} className="text-stone-400 hover:text-rose-600"><X className="h-4 w-4" /></button>
                        </div>
                      ))}
                      <button onClick={() => addOpt(gi)} className="text-xs font-semibold text-orange-700 hover:underline">+ Add option</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <button onClick={save} data-testid="admin-item-save-button" className="w-full rounded-full bg-orange-700 py-3 font-bold text-white hover:bg-orange-800">Save Item</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
